import React, { Fragment, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from '@/assets/app/css/quizResult.module.css';
import FrameBox from '@/shared/components/ui/new/FrameBox';
import StarImage from '@/assets/app/images/adventure/icon_star.png';
import StarEmptyImage from '@/assets/app/images/adventure/icon_empty_star.png';
import CompleteBearImage from '@/assets/app/images/common/bottom/complete-bear.png';
import AppButton from '@/shared/components/ui/new/AppButton';
import useBottomSheetStore from '@/shared/store/UseBottomSheetStore';

interface QuizAnswer {
  questionNumber: number;
  userAnswer: 'correct' | 'incorrect';
  type: 'O' | 'X';
}

interface QuizResultStatusProps {
  completedData: QuizSessionResult;
  chapter?: ChapterData;
  session: QuizSession;
  openRetryQuiz: () => void;
  endAdventure: () => void;
  closeFunc: () => void;
}

interface QuizResultProps {
  completedData: QuizSessionResult;
  chapter?: ChapterData;
  session: QuizSession;
  retryQuiz: Function;
}

const QuizResultStatus: React.FC<QuizResultStatusProps> = ({
  completedData,
  chapter,
  session,
  openRetryQuiz,
  closeFunc,
  endAdventure,
}) => {
  return (
    <div className="page-container page-container--white" style={{ zIndex: 2, padding: 20 }}>
      <button className="page-header__close-btn" onClick={closeFunc}>
        ✕
      </button>

      <div className="page-header">
        <span className="page-header__badge" style={{ color: '#14A1C8' }}>
          {chapter?.chapterTitle}
        </span>
      </div>

      <div className="text-[26px] font-[800] text-center mt-2">결과 확인</div>

      <div className={`${styles['quiz-result__summary']} mt-4`}>
        <span className="font-[800] text-[#0098D8]">정답 {completedData.correctAnswers}</span>,
        <span className="font-[800] text-[#E95550]"> 오답 {completedData.wrongAnswers}</span>
      </div>

      <div className={styles['quiz-result__table-container']}>
        <table className={styles['quiz-result__table']}>
          <thead>
            <tr>
              <th>문항</th>
              <th className={styles['quiz-result__header--correct']}>채점</th>
            </tr>
          </thead>
          <tbody>
            {completedData.answerDetails.map((answer, index) => (
              <tr key={answer.questionId}>
                <td className={styles['quiz-result__question-number']}>{index + 1}</td>
                <td
                  className={`${answer.isCorrect ? styles['quiz-result__header--correct'] : styles['quiz-result__header--incorrect']}`}
                >
                  {answer.isCorrect ? (
                    <span className="font-[800] text-[#0098D8]">O</span>
                  ) : (
                    <span className="font-[800] text-[#E95550]">X</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="button-group" style={{ position: 'fixed', bottom: 30, width: '90%' }}>
        <AppButton onConfirm={endAdventure} message="확인" />
        <AppButton
          backgroundColor="#D4D4D4"
          boxShadowColor="#B7B7B7"
          style={{
            marginTop: 10,
          }}
          onConfirm={openRetryQuiz}
          message="다시 하기"
        />
      </div>
    </div>
  );
};

const QuizResultDetail: React.FC<QuizResultProps> = ({
  completedData,
  session,
  chapter,
  retryQuiz,
}) => {
  const [showResult, setShowResult] = useState(false);
  const { openSheet } = useBottomSheetStore();

  const endAdventure = () => {
    window.location.reload();
  };

  const openRetryQuiz = () => {
    openSheet('retry-quiz', { onConfirm: retryQuiz, onCancel: endAdventure });
  };

  const openStatus = () => {
    setShowResult(true);
  };

  return (
    <Fragment>
      {showResult && (
        <QuizResultStatus
          chapter={chapter}
          session={session}
          completedData={completedData}
          openRetryQuiz={openRetryQuiz}
          endAdventure={endAdventure}
          closeFunc={() => setShowResult(false)}
        />
      )}
      <div className="page-container page-container--white" style={{ padding: 20 }}>
        <div className="page-header">
          <span className="page-header__badge" style={{ color: '#14A1C8' }}>
            {chapter?.chapterTitle}
          </span>
        </div>

        <div className="text-[26px] font-[800] text-center mt-2">
          {session.isRetry ? '다시하기 완료' : '오늘의 모험완료'}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 25 }}>
          <img src={CompleteBearImage} style={{ maxWidth: 'max-content' }} />
        </div>

        <FrameBox
          style={{
            margin: '10px auto',
            fontSize: 20,
            padding: '30px 10px 0',
            display: 'flex',
            minHeight: 200,
            justifyContent: 'center',
          }}
        >
          {!session.isRetry && (
            <>
              <div className="text-[20px] text-[#14A1C8] text-center">
                <span className=" font-[800]">별 포인트 {completedData.earnedPoints}개</span>를
                획득했습니다
              </div>
              <div className="flex justify-center gap-1 mt-2">
                {Array.from({ length: completedData.correctAnswers }).map((_, index) => (
                  <img src={StarImage} />
                ))}
                {Array.from({
                  length: completedData.totalQuestions - completedData.correctAnswers,
                }).map((_, index) => (
                  <img src={StarEmptyImage} />
                ))}
              </div>
            </>
          )}
          <div className="text-[16px] text-center" style={{ marginTop: 40 }}>
            문제 {completedData.totalQuestions}개 중{' '}
            <span className="font-[800] underline text-[#0098D8]" onClick={openStatus}>
              정답 {completedData.correctAnswers}
            </span>
            ,
            <span className="font-[800] underline text-[#E95550]" onClick={openStatus}>
              {' '}
              오답 {completedData.wrongAnswers}
            </span>
          </div>
        </FrameBox>

        <div className="bottom-btn-group">
          <AppButton onConfirm={endAdventure} message="확인" />
          <AppButton
            backgroundColor="#D4D4D4"
            boxShadowColor="#B7B7B7"
            style={{
              marginTop: 10,
            }}
            onConfirm={openRetryQuiz}
            message="다시 하기"
          />
        </div>
      </div>
    </Fragment>
  );
};

export default QuizResultDetail;
